/**
 * Authentication Service
 * Handles login, logout, and token management
 */

import { apiClient, handleApiError } from './apiClient';
import { USERS_API_BASE_URL } from '../constants';

/**
 * Login credentials interface
 */
export interface LoginCredentials {
  email?: string;
  password?: string;
  epubtoken?: string;
  [key: string]: any;
}

/**
 * Login response interface
 */
export interface LoginResponse {
  token: string;
  userData: any;
  [key: string]: any;
}

/**
 * Token refresh request
 */
export interface TokenRefreshRequest {
  token?: string;
  [key: string]: any;
}

/**
 * User login
 * @param credentials - Login credentials (email, password)
 * @returns Promise with login response containing token and user data
 * @throws ApiError if login fails
 */
export const login = async (
  credentials: LoginCredentials
): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post<LoginResponse>(
      `${USERS_API_BASE_URL}/login`,
      credentials
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * ePublisher login
 * @param credentials - ePublisher login credentials
 * @returns Promise with login response containing token and user data
 * @throws ApiError if login fails
 */
export const loginEpub = async (
  credentials: LoginCredentials
): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post<LoginResponse>(
      `${USERS_API_BASE_URL}/loginepub`,
      credentials
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * User logout
 * @param authToken - Authentication token
 * @returns Promise with logout response
 * @throws ApiError if logout fails
 */
export const logout = async (authToken: string): Promise<any> => {
  try {
    const response = await apiClient.get(`${USERS_API_BASE_URL}/logout`, {
      headers: { Authorization: authToken },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Refresh authentication token
 * @param postData - Token refresh request data
 * @returns Promise with new token
 * @throws ApiError if token refresh fails
 */
export const refreshToken = async (
  postData: TokenRefreshRequest
): Promise<{ token: string }> => {
  try {
    const response = await apiClient.post<{ token: string }>(
      `${USERS_API_BASE_URL}/refresh-token`,
      postData
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get current user details
 * @param userUuid - User UUID
 * @param authToken - Authentication token
 * @returns Promise with user details
 * @throws ApiError if request fails
 */
export const getCurrentUser = async (
  userUuid: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/user-detail/${userUuid}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get user permissions
 * @param userUuid - User UUID
 * @param authToken - Authentication token
 * @returns Promise with user permissions
 * @throws ApiError if request fails
 */
export const getUserPermissions = async (
  userUuid: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/user-permission/${userUuid}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get module list
 * @param authToken - Authentication token
 * @returns Promise with module list
 * @throws ApiError if request fails
 */
export const getModuleList = async (
  authToken: string
): Promise<any[]> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/module-list`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
